import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useMutation } from '@apollo/client';
import {
  usePaginatedQuery,
  ErrorHandler,
  ConfirmDialog,
  AUTHENTICATION_SCOPES,
  IfScope,
} from '@playbookux/playbook-web-core';

import {
  filter, find, isEmpty, map,
} from 'lodash';
import { arrayOf, shape, string } from 'prop-types';
import withProjectSidebar from '../../../components/with-project-sidebar';
import { MARK_TEST_SESSION_AS_VIEWED } from '../../../../core/graphql/test-sessions';
import TestTypes from '../../../../core/constants/testTypes';
import ParticipantsSource from '../../../../core/constants/participantsSource';
import { useUserScope } from '../../../../core/hooks';

import Tabs from '../../../constants/tabs';
import { columnsTitles } from './constants';

import ParticipantAccordion from './participant-accordion';
import Uploader from '../../../components/uploader';
import UploadButton from '../../../components/upload-button';

import {
  GET_PROJECT_PARTICIPANTS,
  OFFSET,
  ORDER,
  POOL_INTERVAL,
  QUERY_LIMIT,
  REMOVE_TESTER,
  updateTesterDeleted,
  VALID_BOOKING_STATUS,
} from './graphql';

import {
  ColumnHeader,
  ContainerDropdown,
  ContainerNewText,
  ContainerVideo,
  Dropdown,
  Loader,
  LoadMoreButton,
  Spacer,
  StepHeader,
  Titles,
  UserComponent,
  UploadButtonRow,
} from './styles';
import EmptyResult from '../components/empty-result';

const PROCESSED_STATUS = 'processed';

const ProjectParticipants = (props) => {
  const {
    project,
  } = props;

  const {
    testType,
  } = project;

  const isSurvey = useMemo(() => testType === TestTypes.SURVEY, [testType]);
  const isUpload = useMemo(() => testType === TestTypes.UPLOAD, [testType]);

  const [testerToRemove, setTesterToRemove] = useState('');
  const [participantSegment, setParticipantSegment] = useState('all');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  let isDemoProject = false;
  let projectParticipants = [];
  if (project.id === '00000000-0000-0000-0000-000000000000') {
    isDemoProject = true;
    projectParticipants = filter(project.projectParticipants, (tester) => find(tester.testSessions, {
      approved: true,
    }));
  }
  const scope = useUserScope();

  const {
    data = {},
    loading,
    errors,
    fetchMore,
    refetch,
    stopPolling,
  } = usePaginatedQuery(GET_PROJECT_PARTICIPANTS, {
    variables: {
      projectId: project.id,
      segmentId: participantSegment === 'all' ? null : participantSegment,
      offset: OFFSET,
      limit: QUERY_LIMIT,
      tsIsApproved: true,
      removed: false,
      bookingStatus: VALID_BOOKING_STATUS,
      order: ORDER,
      testType: project.testType,
    },
    pollInterval: POOL_INTERVAL,
  }, {
    dataField: 'projectParticipants',
  });

  useEffect(() => {
    if (!isEmpty(data?.projectParticipants)) {
      stopPolling();
    }
  }, [stopPolling, data]);

  useEffect(() => {
    refetch();
  }, [participantSegment, refetch]);

  const [removeTester, { loading: isRemoveTesterLoading }] = useMutation(REMOVE_TESTER, {
    update: updateTesterDeleted(project.id, testerToRemove, project.testType),
    onCompleted: () => {
      if (isUpload) {
        refetch();
      }
    },
  });

  const [markTestSessionAsViewed] = useMutation(MARK_TEST_SESSION_AS_VIEWED);

  const handleRemoveTester = useCallback(() => {
    removeTester({
      variables: {
        id: testerToRemove,
      },
    }).then(() => {
      setTesterToRemove('');
    });
  }, [removeTester, testerToRemove]);

  const handleMarkTesterAsViewed = (testSessionId) => {
    if (scope === AUTHENTICATION_SCOPES.SHARE_PROJECT) return;

    markTestSessionAsViewed({
      variables: {
        testSessionId,
      },
    });
  };

  const dataTesters = useMemo(() => {
    if (!data?.projectParticipants) return undefined;
    return data?.projectParticipants?.filter((participant) => {
      if (participant.testSessions.filter((session) => session.status === PROCESSED_STATUS || isUpload).length > 0) {
        return true;
      }
      return false;
    });
  }, [data?.projectParticipants, isUpload]);

  const segmentsDropdownOptions = useMemo(() => ([
    { key: 1, text: 'All audience segments', value: 'all' },
    ...map(project.segments, (segment) => ({
      key: segment.id,
      text: segment.name,
      value: segment.id,
    })),
  ]), [project.segments]);
  const hasMoreThanOneSegment = project.segments && project.segments.length > 1;

  return (
    <UserComponent paddingSize={hasMoreThanOneSegment}>
      <StepHeader>
        {errors && (
          <ErrorHandler
            error={errors}
            defaultMessage="Error while fetching participants."
          />
        )}

        {hasMoreThanOneSegment && (
          <ContainerDropdown>
            <Dropdown
              placeholder="All audience segments"
              selection
              value={participantSegment}
              options={segmentsDropdownOptions}
              onChange={(e, d) => setParticipantSegment(d.value)}
            />
          </ContainerDropdown>
        )}

        {isEmpty(dataTesters) && !isDemoProject && !loading && (
          <EmptyResult testType={testType} onUploadClick={handleOpenUploadModal} />
        )}

        <ConfirmDialog
          handleConfirm={handleRemoveTester}
          title={isSurvey ? 'Delete submission?' : 'Delete video?'}
          warning="Warning: This cannot be undone"
          confirmText={isSurvey ? 'Delete' : 'Yes, delete video'}
          cancelText={isSurvey ? 'Cancel' : 'Cancel, keep video'}
          show={!isEmpty(testerToRemove)}
          loading={isRemoveTesterLoading}
          loadingConfirmButton={isRemoveTesterLoading}
          handleClose={() => setTesterToRemove('')}
        />

        {(!isEmpty(dataTesters) && isUpload) && (
          <IfScope user scope={scope}>
            <UploadButtonRow>
              <UploadButton text="Upload" handleOpenUploader={handleOpenUploadModal} />
            </UploadButtonRow>
          </IfScope>
        )}

        {!isEmpty(dataTesters)
          && project.participantsSource === ParticipantsSource.PLAYBOOK
          && testType !== TestTypes.CARD_SORT
          && (
            <Titles>
              <ContainerNewText>
                <Spacer />
              </ContainerNewText>
              <ColumnHeader />
              {columnsTitles.map((columnsTitle) => (
                <ColumnHeader key={columnsTitle.id} index={columnsTitle.id} flexSize={columnsTitle.flexSize}>
                  {columnsTitle.title}
                </ColumnHeader>
              ))}
              <ColumnHeader />
              <ContainerVideo />
            </Titles>
          )}

        {!isEmpty(dataTesters) && !loading && dataTesters?.map((tester, index) => (
          <ParticipantAccordion
            index={index}
            key={tester.id}
            project={project}
            tester={tester}
            onClickDelete={(id) => setTesterToRemove(id)}
            onOpenTestSession={() => handleMarkTesterAsViewed(tester.testSessions[0].id)}
            refetch={refetch}
            scope={scope}
          />
        ))}

        {!isEmpty(projectParticipants) && !loading && projectParticipants.map((tester, index) => (
          <ParticipantAccordion
            index={index}
            key={tester.id}
            project={project}
            tester={tester}
            onClickDelete={(id) => setTesterToRemove(id)}
            onOpenTestSession={() => handleMarkTesterAsViewed(tester.testSessions[0].id)}
            refetch={refetch}
            scope={scope}
          />
        ))}

        {!loading && !isEmpty(data) && data.testersCount > data.projectParticipants.length
          && data.projectParticipants.length > 0
          && <LoadMoreButton onClick={fetchMore}>View More</LoadMoreButton>}

        {loading && (
          <Loader data-loader="participants-loader" />
        )}

        {isUploadModalOpen && (
          <Uploader
            title="Upload Research"
            onClose={handleCloseUploadModal}
            show={isUploadModalOpen}
            projectId={project.id}
            onUploadCompleted={() => {
              refetch();
            }}
            companyId={project.companyId}
          />
        )}
      </StepHeader>
    </UserComponent>
  );
};

ProjectParticipants.propTypes = {
  project: shape({
    id: string,
    projectParticipants: arrayOf(shape({})),
    segments: arrayOf(shape({})),
    testType: string,
    participantsSource: string,
    companyId: string,
  }).isRequired,
};

export default withProjectSidebar({ selectedTab: Tabs.PROJECT })(ProjectParticipants);
